import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { ProgramDraft } from '@/types/program';
import { programDraftsApi } from '@/services/programDrafts';

export const useProgramDrafts = () => {
  const [drafts, setDrafts] = useState<ProgramDraft[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all drafts
  const fetchDrafts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await programDraftsApi.getAllDrafts();
      setDrafts(response.data.drafts);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch drafts';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Create new draft
  const createDraft = useCallback(async (programData: ProgramDraft['programData']) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await programDraftsApi.createDraft(programData);
      setDrafts((prevDrafts) => [...prevDrafts, response.data.draft]);
      toast.success('Draft created successfully');
      return response.data.draft;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create draft';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update existing draft
  const updateDraft = useCallback(async (draftId: string, programData: ProgramDraft['programData']) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await programDraftsApi.updateDraft(draftId, programData);
      setDrafts((prevDrafts) =>
        prevDrafts.map((draft) =>
          draft._id === draftId ? response.data.draft : draft
        )
      );
      toast.success('Draft updated successfully');
      return response.data.draft;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update draft';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Delete draft
  const deleteDraft = useCallback(async (draftId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      // Optimistically remove the draft from the UI
      setDrafts((prevDrafts) => prevDrafts.filter((draft) => draft._id !== draftId));
      
      await programDraftsApi.deleteDraft(draftId);
      toast.success('Draft deleted successfully');
    } catch (err) {
      // Revert the optimistic update on error
      fetchDrafts();
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete draft';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [fetchDrafts]);

  // Bulk delete drafts
  const bulkDeleteDrafts = useCallback(async (draftIds: string[]) => {
    setIsLoading(true);
    setError(null);
    try {
      // Optimistically remove the drafts from the UI
      setDrafts((prevDrafts) => prevDrafts.filter((draft) => !draftIds.includes(draft._id)));
      
      await programDraftsApi.bulkDeleteDrafts(draftIds);
      toast.success(`${draftIds.length} draft${draftIds.length === 1 ? '' : 's'} deleted successfully`);
    } catch (err) {
      // Revert the optimistic update on error
      fetchDrafts();
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete drafts';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [fetchDrafts]);

  return {
    drafts,
    isLoading,
    error,
    fetchDrafts,
    createDraft,
    updateDraft,
    deleteDraft,
    bulkDeleteDrafts,
  };
};